import axios from "axios";

class AxiosClient {
  constructor() {
    const API_BASE_URL =
      import.meta.env.VITE_API_URL || "http://localhost:3000/api";

    this.instance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        // "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    this.refreshPromise = null;
    this.setupInterceptors();
  }

  getAccessToken() {
    if (typeof window !== "undefined") {
      const token =
        localStorage.getItem("accessToken") ||
        localStorage.getItem("userToken") ||
        localStorage.getItem("adminToken");

      return token;
    }
    return null;
  }

  getRefreshToken() {
    if (typeof window !== "undefined") {
      return localStorage.getItem("refreshToken");
    }
    return null;
  }

  setTokens(accessToken, refreshToken) {
    if (typeof window !== "undefined") {
      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("adminToken", accessToken);
      localStorage.setItem("userToken", accessToken);
      if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
        localStorage.setItem("adminRefreshToken", refreshToken);
        localStorage.setItem("userRefreshToken", refreshToken);
      }
    }
  }

  clearTokens() {
    if (typeof window !== "undefined") {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      localStorage.removeItem("admin");
      localStorage.removeItem("userToken");
      localStorage.removeItem("adminToken");
      localStorage.removeItem("userRefreshToken");
      localStorage.removeItem("adminRefreshToken");
      localStorage.removeItem("isAdminAuthenticated");
    }
  }

  setupInterceptors() {
    // Request interceptor
    this.instance.interceptors.request.use(
      (config) => {
        const method = config.method?.toUpperCase() || "GET";

        // Public endpoints that don't need authentication
        const publicEndpoints = [
          "/products",
          "/products/featured",
          "/products/on-sale",
          "/products/new",
          "/products/search",
          "/admins/login",
          "/users/login",
          "/users/register",
          "/blogs/featured",
          "/blogs/popular",
          "/blogs/trending",
          "/blogs/search",
          "/blogs/archive",
          "/blogs/popular-tags",
        ];

        // Admin endpoints that NEED authentication
        // Check for exact matches or patterns
        const isAdminEndpoint = () => {
          const url = config.url;

          // Exact admin paths
          const exactPaths = [
            "/products/stats",
            "/products/category-distribution",
            "/products/frequently-requested",
            "/products/unpopular",
            "/products/date-range",
            "/products/bulk-delete",
            "/products/bulk-status",
            "/products/bulk-featured",
            "/products/bulk-new",
            "/products/bulk-availability",
            "/admin/blogs",
            "/admin/blogs/stats",
            "/admin/blogs/categories",
          ];

          if (exactPaths.some((path) => url === path)) {
            return true;
          }

          // Admin blog endpoints with ID
          if (url?.match(/^\/admin\/blogs\/\d+$/)) {
            return true;
          }

          // Admin blog operations (publish, schedule, archive, restore, etc.)
          if (
            url?.match(
              /^\/admin\/blogs\/\d+\/(publish|schedule|archive|restore|toggle-featured|toggle-popular|toggle-trending|related|generate-toc|poll)$/,
            )
          ) {
            return true;
          }

          // Product admin operations (update, delete, toggle, etc.)
          if (method !== "GET" && url?.match(/^\/products\/\d+$/)) {
            return true;
          }

          // Product toggle operations
          if (
            url?.match(
              /^\/products\/\d+\/(toggle-featured|toggle-new|toggle-availability|toggle-active)$/,
            )
          ) {
            return true;
          }

          // Product image operations
          if (
            url?.match(
              /^\/products\/\d+\/(main-image|gallery|gallery\/reorder)$/,
            )
          ) {
            return true;
          }

          // Product duplicate
          if (url?.match(/^\/products\/\d+\/duplicate$/)) {
            return true;
          }

          return false;
        };

        const isPublicEndpoint = publicEndpoints.some((endpoint) =>
          config.url.includes(endpoint),
        );

        const isAdmin = isAdminEndpoint();

        // For product creation, it's NOT public
        const isCreateProduct =
          config.method === "post" && config.url === "/products";
        const isCreateRequest =
          config.method === "post" && config.url === "/requests";

        // IMPORTANT: Admin endpoints ALWAYS need authentication
        if (isAdmin) {
          const token = this.getAccessToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          } else {
            console.warn(
              "⚠️ No token available for admin endpoint:",
              config.url,
            );
          }
          config.metadata = { startTime: Date.now() };
          return config;
        }

        if (
          (isPublicEndpoint && !isCreateProduct && !isCreateRequest) ||
          config.url === "/users/logout" ||
          config.url === "/admins/logout"
        ) {
          config.metadata = { startTime: Date.now() };
          return config;
        }

        const token = this.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          console.warn("⚠️ No token available for request");
        }

        config.metadata = { startTime: Date.now() };
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // Response interceptor
    this.instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const isBlogEndpoint = originalRequest?.url?.includes("/blogs");
        const isPasswordChangeEndpoint =
          originalRequest?.url?.includes("/change-password");
        const isLoginEndpoint = originalRequest?.url?.includes("/login");
        const isRegisterEndpoint = originalRequest?.url?.includes("/register");

        const isUserEndpoint =
          originalRequest?.url?.includes("/users") ||
          originalRequest?.url?.includes("/requests") ||
          originalRequest?.url?.includes("/notifications") ||
          originalRequest?.url?.includes("/wishlist") ||
          originalRequest?.url?.includes("/settings");

        // Don't redirect for blog endpoints - public access
        if (isBlogEndpoint && error.response?.status === 401) {
          return Promise.reject(error);
        }

        // For password change with incorrect password - don't redirect, just return the error as is
        if (isPasswordChangeEndpoint && error.response?.status === 401) {
          return Promise.reject(error);
        }

        // For login/register endpoints - don't redirect
        if (
          (isLoginEndpoint || isRegisterEndpoint) &&
          error.response?.status === 401
        ) {
          return Promise.reject(error);
        }

        // Handle token expiration for user endpoints (only for non-password-change endpoints)
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          // For user endpoints, try to refresh the token first
          if (isUserEndpoint && !isPasswordChangeEndpoint) {
            try {
              const refreshToken =
                localStorage.getItem("refreshToken") ||
                localStorage.getItem("userRefreshToken");

              if (refreshToken) {
                const response = await this.instance.post(
                  "/users/refresh-token",
                  {
                    refreshToken,
                  },
                );

                if (response.data?.token) {
                  const newToken = response.data.token;
                  const newRefreshToken = response.data.refreshToken;

                  // Store new tokens
                  localStorage.setItem("accessToken", newToken);
                  localStorage.setItem("userToken", newToken);
                  if (newRefreshToken) {
                    localStorage.setItem("refreshToken", newRefreshToken);
                    localStorage.setItem("userRefreshToken", newRefreshToken);
                  }

                  // Retry the original request with new token
                  originalRequest.headers.Authorization = `Bearer ${newToken}`;
                  return this.instance(originalRequest);
                }
              }
            } catch (refreshError) {
              console.error("Token refresh failed:", refreshError);
            }

            // If refresh failed, clear tokens and redirect to login
            localStorage.removeItem("accessToken");
            localStorage.removeItem("userToken");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("userRefreshToken");
            localStorage.removeItem("user");
            localStorage.removeItem("isAdminAuthenticated");

            if (
              typeof window !== "undefined" &&
              !window.location.pathname.includes("/user/login")
            ) {
              window.location.href = "/user/login";
            }
            return Promise.reject(error);
          }

          // For admin endpoints, redirect to admin login
          if (
            originalRequest?.url?.includes("/admin") ||
            originalRequest?.url?.includes("/products/stats") ||
            originalRequest?.url?.includes("/products/category-distribution") ||
            originalRequest?.url?.match(/^\/products\/\d+$/)
          ) {
            localStorage.removeItem("accessToken");
            localStorage.removeItem("adminToken");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("adminRefreshToken");
            localStorage.removeItem("admin");
            localStorage.removeItem("isAdminAuthenticated");

            if (
              typeof window !== "undefined" &&
              !window.location.pathname.includes("/admin/login")
            ) {
              window.location.href = "/admin/login";
            }
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      },
    );
  }

  async refreshAccessToken() {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.clearTokens();
      if (typeof window !== "undefined") {
        window.location.href = "/user/login";
      }
      return null;
    }

    this.refreshPromise = this.instance
      .post("/auth/refresh-token", { refreshToken })
      .catch(() => {
        // If first endpoint fails, try another
        return this.instance.post("/users/refresh-token", { refreshToken });
      })
      .then((response) => {
        const tokenData = response.data?.data || response.data;
        const { token, accessToken, refreshToken: newRefreshToken } = tokenData;

        const newToken = token || accessToken;
        if (newToken) {
          this.setTokens(newToken, newRefreshToken || refreshToken);
          return newToken;
        } else {
          throw new Error("No token in refresh response");
        }
      })
      .catch((error) => {
        this.clearTokens();
        if (typeof window !== "undefined") {
          window.location.href = "/user/login";
        }
        throw error;
      })
      .finally(() => {
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  normalizeError(error) {
    if (error.response) {
      const data = error.response.data;

      // Handle HTML error responses
      if (typeof data === "string" && data.includes("<!DOCTYPE html>")) {
        return new Error(
          `Server error: ${error.response.status}. Please try again.`,
        );
      }

      if (data.errors && Array.isArray(data.errors)) {
        const firstError = data.errors[0];
        return new Error(
          firstError.msg || firstError.message || "Validation error",
        );
      }

      return new Error(
        data.message || data.error || `Server error: ${error.response.status}`,
      );
    } else if (error.request) {
      return new Error("Network error. Please check if the server is running.");
    } else {
      return new Error(error.message || "An unexpected error occurred");
    }
  }

  // Public methods
  async get(url, config = {}) {
    const response = await this.instance.get(url, config);
    return response.data;
  }

  async post(url, data = {}, config = {}) {
    const fullUrl = `${this.instance.defaults.baseURL}${url}`;

    const response = await this.instance.post(url, data, config);
    return response.data;
  }

  async put(url, data = {}, config = {}) {
    const response = await this.instance.put(url, data, config);
    return response.data;
  }

  async patch(url, data = {}, config = {}) {
    const response = await this.instance.patch(url, data, config);
    return response.data;
  }

  async delete(url, config = {}) {
    const response = await this.instance.delete(url, config);
    return response.data;
  }

  async upload(url, formData, config = {}, method = "post") {
    const response = await this.instance[method](url, formData, {
      ...config,
      headers: {
        ...config.headers,
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  }
}

export const apiClient = new AxiosClient();
